import {
  Box,
  Button,
  InputLabel,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import React, { FC, useEffect, useState } from "react";
import { ContentLayout } from "../../layout/contentLayout";
import ImageBackground from "../ui-elements/image-background";
import { RequiredIcon } from "../ui-elements/svg-icons";
import { useNavigate } from "react-router-dom";
import { PATH_ACCOUNT, PATH_REPS } from "../../routes/path";
import { InputCustom } from "../ui-elements/input-ui";
import SelectInput from "../ui-elements/select";
import { IconText } from "../ui-elements/ui-content";
import { labelStyles } from "../ui-elements/styles-input";
import { AccountProfileData } from "../../interface/account";
import { getLgaById, getStates, SelectOption } from "../../utils/adhoc-request";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UpdateProfileProps } from "../../interface/api";
import { useNotificationService } from "../../hooks/notification";
import { apiUpdateProfile } from "../../api/account";
import useResponsive from "../../hooks/useResponsive";
import { LoadingButton } from "@mui/lab";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import {
  DeleteAccountModal,
  LogoutModal,
  RepsAccountOnboarding,
} from "./modal-content";

const EditContent: FC<{
  account_profile_data: AccountProfileData;
}> = ({ account_profile_data }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMedium = useResponsive("down", "md", "xl");
  const { showNotification } = useNotificationService();
  //  modal state
  const [open_modal, setOpen_modal] = useState(false);
  const [open_delete, setOpen_delete] = useState(false);
  const [open_logout, setOpen_logout] = useState(false);
  //
  const [states, setStates] = useState<SelectOption[]>([]);
  const [lga, setLga] = useState<SelectOption[]>([]);
  const [update_data, setUpdate_data] = useState({
    name: account_profile_data.name,
    phone_number: account_profile_data?.phone_number,
    email: account_profile_data?.email,
    state: account_profile_data?.state,
    local_government: account_profile_data?.local_government,
    local_government_Id: 0,
    state_id: 0,
  });
  //
  const badge = useSelector((state: RootState) => state.content.badge_number);

  const verification_status = useSelector(
    (state: RootState) => state.content.verification_status
  );
  const fetch_states = async () => {
    try {
      const states_data = await getStates();
      setStates(states_data);
      const state_digit = states_data.find(
        (state) => state.label === account_profile_data?.state
      )?.value;
      setUpdate_data((prev) => ({ ...prev, state_id: Number(state_digit) }));
    } finally {
    }
  };

  const fetch_lga = async () => {
    try {
      const lgaData = await getLgaById(update_data?.state_id.toString());
      setLga(lgaData);
    } finally {
    }
  };

  useEffect(() => {
    if (states.length === 0) {
      fetch_states();
    }
    if (update_data?.state_id != 0) {
      fetch_lga();
    }
  }, [update_data?.state_id]);

  const mutation = useMutation({
    mutationFn: (fields: UpdateProfileProps) => apiUpdateProfile(fields),
    onSuccess: async (data) => {
      if (data?.status === "error") {
        if ("details" in data) {
          showNotification(data?.details ?? "An error has occurred", "error");
          return;
        }
        return;
      }
      if (data.status === 200) {
        showNotification("Profile updated successfully", "success");
        queryClient.invalidateQueries({ queryKey: ["account-profile"] });
        navigate(PATH_ACCOUNT.root);
      }
    },
    onError: (error) => {
      showNotification("Server error", "error");
    },
  });

  //handle account submission
  const handleUpdate = async () => {
    // handle account submission logic here

    const data = {
      name: update_data.name,
      phone_number: update_data.phone_number,
      email: update_data.email,
      state_id: update_data.state_id,
      local_government_id: update_data.local_government_Id,
    };

    if (
      data.local_government_id === 0 ||
      data.local_government_id === undefined
    ) {
      showNotification("Please select a local government", "error");
      return;
    }
    mutation.mutate(data);
  };
  // handle reps
  const handle_reps = () => {
    if (verification_status === "not-complete") {
      setOpen_modal(true);
    } else {
      navigate(PATH_REPS.root);
    }
  };
  const toggle_modal = () => setOpen_modal(!open_modal);
  const toggle_delete = () => setOpen_delete(!open_delete);
  const toggle_logout = () => setOpen_logout(!open_logout);

  return (
    <ContentLayout>
      <RepsAccountOnboarding open={open_modal} handle_modal={toggle_modal} />
      <DeleteAccountModal open={open_delete} handle_modal={toggle_delete} />
      <LogoutModal open={open_logout} handle_modal={toggle_logout} />
      <Box
        id="main-scroll-container"
        sx={{
          maxWidth: 605,
          mx: "auto",
          py: "80px",
        }}
      >
        <ImageBackground
          sourceCover={
            account_profile_data?.cover_photo_url ??
            "/assets/images/account/cover-bg.png"
          }
          account={false}
          sourceProfile={
            account_profile_data?.photo_url ??
            "/assets/images/account/cover-bg.jpeg"
          }
        />
        <Stack
          direction={"row"}
          justifyContent={"flex-end"}
          sx={{ my: isMedium ? "4rem" : 3, mb: 3 }}
          spacing={2}
        >
          {badge === 2 ? (
            <Button
              variant="outlined"
              sx={{
                borderRadius: "43px",
                minWidth: "120px",
                height: "2.5rem",
              }}
              onClick={() => {}}
            >
              {" "}
              Resign office
            </Button>
          ) : (
            <Button
              variant="outlined"
              sx={{
                borderRadius: "43px",
                minWidth: "120px",
                height: "2.5rem",
              }}
              onClick={handle_reps}
            >
              {" "}
              Apply as a Representative
            </Button>
          )}
        </Stack>
        <Stack sx={{}}>
          <Stack direction={"row"} spacing={1} alignItems={"center"}>
            <Typography variant="body1" color="black" sx={{ fontWeight: 600 }}>
              Personal Information
            </Typography>
          </Stack>
          <Stack sx={{ my: 1 }}>
            <InputLabel sx={labelStyles}>
              Full name <RequiredIcon />
            </InputLabel>
            <InputCustom
              placeholder={""}
              value={update_data?.name ?? ""}
              onchange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setUpdate_data((prev) => ({ ...prev, name: e.target.value }))
              }
              type={""}
              styles={{}}
              size={undefined}
            />
          </Stack>
          <Stack sx={{ my: 1 }}>
            <InputLabel sx={labelStyles}>
              Email <RequiredIcon />
            </InputLabel>
            <InputCustom
              placeholder={""}
              value={update_data?.email ?? ""}
              onchange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setUpdate_data((prev) => ({ ...prev, email: e.target.value }))
              }
              type={""}
              styles={undefined}
              size={undefined}
            />
          </Stack>
          <Stack sx={{ my: 1 }}>
            <InputLabel sx={labelStyles}>
              Phone <RequiredIcon />
            </InputLabel>
            <InputCustom
              placeholder={""}
              value={update_data?.phone_number ?? ""}
              onchange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setUpdate_data((prev) => ({
                  ...prev,
                  phone_number: e.target.value,
                }))
              }
              type={""}
              styles={undefined}
              size={undefined}
            />
          </Stack>
          <Typography
            variant="body1"
            sx={{ color: "#0A0B0A", fontWeight: 600 }}
          >
            Location
          </Typography>
          <Stack sx={{ my: 1 }}>
            <InputLabel sx={labelStyles}>
              State of Primary Residence <RequiredIcon />
            </InputLabel>
            <SelectInput
              value={
                states?.find((state) => state.value === update_data?.state_id)
                  ?.value
              }
              onChange={(e: SelectChangeEvent<any>) =>
                setUpdate_data((prev) => ({
                  ...prev,
                  state_id: e.target.value,
                }))
              }
              options={states}
            />
          </Stack>
          <Stack sx={{ my: 1 }}>
            <InputLabel sx={labelStyles}>
              {" "}
              Local Government of Primary Residence
              <RequiredIcon />
            </InputLabel>
            {
              <SelectInput
                value={
                  lga?.find(
                    (lg) => lg.value === update_data?.local_government_Id
                  )?.value
                }
                onChange={(e: SelectChangeEvent<any>) =>
                  setUpdate_data((prev) => ({
                    ...prev,
                    local_government_Id: e.target.value,
                  }))
                }
                options={lga}
              />
            }
          </Stack>
          <LoadingButton
            onClick={handleUpdate}
            loading={mutation.isPending}
            variant="contained"
            sx={{ my: 2 }}
          >
            Update Profile
          </LoadingButton>
        </Stack>

        <Stack>
          <IconText
            image={"/assets/icons/account/question-mark.png"}
            text={"FAQ"}
            onClick={() => navigate(PATH_ACCOUNT.faq)}
          />
          <IconText
            image={"/assets/icons/account/side-in.png"}
            text={"Terms and condition"}
            onClick={() => navigate(PATH_ACCOUNT.terms)}
          />
          <IconText
            image={"/assets/icons/account/side-in.png"}
            text={"Privacy policy"}
            onClick={() => navigate(PATH_ACCOUNT.privacy)}
          />
          <IconText
            image={"/assets/icons/account/side-in.png"}
            text={"Log out"}
            onClick={toggle_logout}
          />
          <IconText
            image={"/assets/icons/account/side-inRed.png"}
            text={"Delete my account and data"}
            onClick={toggle_delete}
          />
        </Stack>
      </Box>
    </ContentLayout>
  );
};

export default EditContent;
